// src/components/ChatPopup.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../axiosInstance';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import { usePairing } from '../context/PairingContext';
import { Paperclip, X, Loader2, FileText, FileSpreadsheet, Presentation, Download } from 'lucide-react';

/* --------------------------- Upload-Konstanten --------------------------- */
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.pptx', '.xlsx', '.jpg', '.jpeg', '.png'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
];

function formatFileSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/* --------------------------- File-Attachment-Rendering --------------------------- */
function getFileIcon(mimeType) {
  if (!mimeType) return FileText;
  if (mimeType === 'application/pdf') return FileText;
  if (mimeType.includes('spreadsheetml')) return FileSpreadsheet;
  if (mimeType.includes('presentationml')) return Presentation;
  if (mimeType.includes('wordprocessingml')) return FileText;
  return FileText;
}

// Farbkodierung für File-Icons (dunkel, da Chat-Bubbles hellen Hintergrund haben)
function getFileIconColor(mimeType) {
  if (!mimeType) return 'text-gray-600';
  if (mimeType === 'application/pdf') return 'text-red-600';
  if (mimeType.includes('spreadsheetml')) return 'text-green-600';
  if (mimeType.includes('presentationml')) return 'text-orange-600';
  if (mimeType.includes('wordprocessingml')) return 'text-blue-600';
  return 'text-gray-600';
}

function isImageMime(mimeType) {
  return mimeType === 'image/jpeg' || mimeType === 'image/png';
}

function ChatFileAttachment({ file }) {
  if (!file) return null;

  // Staging/Prod: Frontend + API laufen über denselben Host (nginx-Reverse-Proxy),
  // deshalb reicht die relative download_url. Falls das ändert: Base-URL-Helper ziehen.
  const downloadHref = file.download_url;

  if (isImageMime(file.mime_type)) {
    return (
      <div className="mt-1">
        <a href={downloadHref} target="_blank" rel="noopener noreferrer">
          <img
            src={downloadHref}
            alt={file.original_name}
            className="max-w-[240px] max-h-[240px] rounded cursor-pointer"
            loading="lazy"
          />
        </a>
        <div
          className="text-xs opacity-70 mt-1 truncate max-w-[240px]"
          title={file.original_name}
        >
          {file.original_name}
        </div>
      </div>
    );
  }

  const IconComp = getFileIcon(file.mime_type);
  const iconColor = getFileIconColor(file.mime_type);

  return (
    <a
      href={downloadHref}
      target="_blank"
      rel="noopener noreferrer"
      download={file.original_name}
      className="flex items-center gap-2 p-2 mt-1 rounded bg-black/10 hover:bg-black/20 transition-colors no-underline max-w-[240px] text-black"
      title={`${file.original_name}${file.file_size != null ? ` (${formatFileSize(file.file_size)})` : ''}`}
    >
      <IconComp size={24} className={`flex-shrink-0 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{file.original_name}</div>
        {file.file_size != null && (
          <div className="text-xs opacity-70">{formatFileSize(file.file_size)}</div>
        )}
      </div>
      <Download size={16} className="flex-shrink-0 opacity-60" />
    </a>
  );
}

/* --------------------------- Zeit/Helfer --------------------------- */
function formatTime(ts) {
  const d = ts ? new Date(ts) : new Date();
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function formatDateLabel(ts) {
  const d = ts ? new Date(ts) : new Date();
  return d.toLocaleDateString();
}
function convertEmojis(text = '') {
  return text
    .replace(/:smile:/g, '😄')
    .replace(/:heart:/g, '❤️')
    .replace(/:thumbsup:/g, '👍')
    .replace(/:laugh:/g, '😂');
}
const EMOJI_LIST = ['😄', '❤️', '👍', '😂'];

const isHr = (v) => String(v ?? '').toLowerCase() === 'hr';
const toNumOrNull = (v) => {
  if (v === null || v === undefined || v === 'null') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const rolesOf = (m) => ({
  s: String(m.sender_role ?? m.senderRole ?? m.sender ?? '').toLowerCase(),
  r: String(m.receiver_role ?? m.receiverRole ?? m.receiver ?? '').toLowerCase(),
});
const idsOf = (m) => ({
  menteeId: toNumOrNull(m.mentee_id ?? m.menteeId),
  mentorId: toNumOrNull(m.mentor_id ?? m.mentorId),
});

async function safeGet(url) {
  try {
    const { data } = await axios.get(url, { withCredentials: true, timeout: 10000 });
    return data;
  } catch {
    return null;
  }
}

/* --------------------- Namen robust nachladen ---------------------- */
function pickNameFromAny(raw) {
  if (!raw) return '';
  const src =
    raw.data ??
    raw.user ??
    raw.profile ??
    raw.hr_profile ??
    raw.mentee ??
    raw.mentor ??
    raw;
  const first =
    src.first_name ??
    src.firstName ??
    src.given_name ??
    src.givenName ??
    src.firstname ??
    '';
  const last =
    src.last_name ??
    src.lastName ??
    src.family_name ??
    src.familyName ??
    src.lastname ??
    '';
  const display = src.display_name ?? src.displayName ?? src.name ?? '';
  const full = `${(first || '').trim()} ${(last || '').trim()}`.trim();
  return (full || display || '').trim();
}

async function fetchMenteeName(menteeId) {
  if (!menteeId) return '';

  // 1) neue, stabile Name-Route
  const direct = await safeGet(`/mentoring/chat/user-name?menteeId=${menteeId}`);
  if (direct?.name) return direct.name;

  // 2) Fallbacks (bestehende Endpunkte)
  const urls = [
    `/mentoring/mentee/${menteeId}`,
    `/mentoring/mentee/${menteeId}/profile`,
    `/mentoring/users/${menteeId}`,
    `/mentoring/user/${menteeId}`,
    `/mentoring/mentee/profile?id=${menteeId}`,
  ];
  for (const u of urls) {
    const data = await safeGet(u);
    const name = pickNameFromAny(data);
    if (name) return name;
  }
  return '';
}

async function fetchMentorName(mentorId) {
  if (!mentorId) return '';

  // 1) neue, stabile Name-Route
  const direct = await safeGet(`/mentoring/chat/user-name?mentorId=${mentorId}`);
  if (direct?.name) return direct.name;

  // 2) Fallbacks
  const urls = [
    `/mentoring/mentor/${mentorId}`,
    `/mentoring/mentor/${mentorId}/profile`,
    `/mentoring/users/${mentorId}`,
    `/mentoring/user/${mentorId}`,
    `/mentoring/mentor/profile?id=${mentorId}`,
  ];
  for (const u of urls) {
    const data = await safeGet(u);
    const name = pickNameFromAny(data);
    if (name) return name;
  }
  return '';
}

/** Aktiven/letzten Mentee zu einem Mentor ermitteln (nur falls benötigt). */
async function resolveActiveMenteeIdForMentor(mentorId, matchingId) {
  if (!mentorId) return null;
  const data = await safeGet(`/mentoring/mentor/${mentorId}/matched-mentees${matchingId ? `?matching_id=${matchingId}` : ''}`);
  if (!data) return null;
  const list = Array.isArray(data) ? data : [data];
  if (list.length === 0) return null;
  const score = (x) =>
    (x.confirmed_by_hr ? 1 : 0) +
    (x.confirmed_by_mentee ? 1 : 0) +
    (String(x.status || '').toLowerCase() === 'confirmed' ? 1 : 0);
  const first = [...list].sort((a, b) => score(b) - score(a))[0] || list[0];
  return toNumOrNull(first?.mentee_id ?? first?.user_id);
}

/* ------------------------------------------------------------------ */

export default function ChatPopup({
  menteeId,
  mentorId,
  senderRole,
  receiverRole,
  presetMessage = '',
  onClose,
  /** optional: 'hr' | 'pair' – erzwingt Modus */
  threadType,
  /** optional: Überschrift im Header (z. B. "Chat mit Mentee Max Mustermann") */
  title,
}) {
  const { t } = useTranslation();
  const { selectedMatchingId } = usePairing();

  /* IDs robust normalisieren */
  const ids = useMemo(
    () => ({
      menteeId: toNumOrNull(menteeId),
      mentorId: toNumOrNull(mentorId),
    }),
    [menteeId, mentorId]
  );

  /* Rollen */
  const roleS = String(senderRole || '').toLowerCase();
  const roleR = String(receiverRole || '').toLowerCase();

  /* Modus bestimmen (HR-Thread vs. Pair) */
  const modeHR = useMemo(() => {
    if (threadType === 'hr') return true;
    if (threadType === 'pair') return false;
    const hrInvolved = isHr(senderRole) || isHr(receiverRole);
    if (hrInvolved && (!ids.menteeId || !ids.mentorId)) return true;
    return false;
  }, [threadType, senderRole, receiverRole, ids.menteeId, ids.mentorId]);

  /**
   * Kanal strikt erzwingen:
   * - Mentor↔HR: nur Mentor-Kanal
   * - Mentee↔HR: nur Mentee-Kanal
   */
  const channelMentorHR =
    modeHR && ((roleS === 'mentor' && roleR === 'hr') || (roleS === 'hr' && roleR === 'mentor'));
  const channelMenteeHR =
    modeHR && ((roleS === 'mentee' && roleR === 'hr') || (roleS === 'hr' && roleR === 'mentee'));

  /* State */
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState(presetMessage || '');
  const [errorText, setErrorText] = useState('');
  const [resolvedMenteeId, setResolvedMenteeId] = useState(null);
  const [headerTitle, setHeaderTitle] = useState(title || '');
  const chatEndRef = useRef(null);

  /* --- File-Upload State --- */
  const fileInputRef = useRef(null);
  const [attachedFile, setAttachedFile] = useState(null); // { file: File, previewSize: string }
  const [uploading, setUploading] = useState(false);

  /* Draggable */
  const [position, setPosition] = useState({ top: 100, left: 100 });
  const [dragging, setDragging] = useState(false);
  const offset = useRef({ x: 0, y: 0 });

  /* Preset aktualisieren */
  useEffect(() => {
    setNewMessage(presetMessage || '');
  }, [presetMessage]);

  /* ----------------------- Titel automatisch (Name/HR) ----------------------- */
  useEffect(() => {
    let stop = false;

    async function resolveTitle() {
      // 1) expliziter Titel gewinnt
      if (title && String(title).trim()) {
        setHeaderTitle(title.trim());
        return;
      }

      // Bin ich HR?
      const amIHR = roleS === 'hr';

      // 2) HR-Threads
      if (modeHR) {
        // Nicht-HR (Mentor oder Mentee) spricht mit HR => immer "Chat mit HR"
        if (!amIHR && roleR === 'hr') {
          if (!stop) setHeaderTitle(t('chatPopup.chatWithHR', 'Chat mit HR'));
          return;
        }

        // HR spricht mit Mentor => Namen des Mentors anzeigen
        if (amIHR && channelMentorHR) {
          if (ids.mentorId) {
            const name = await fetchMentorName(ids.mentorId);
            if (!stop) setHeaderTitle(name ? `Chat mit Mentor ${name}` : `Chat mit Mentor #${ids.mentorId}`);
            return;
          }
        }

        // HR spricht mit Mentee => Namen des Mentees anzeigen
        if (amIHR && channelMenteeHR) {
          const mid = ids.menteeId ?? resolvedMenteeId ?? null;
          if (mid) {
            const name = await fetchMenteeName(mid);
            if (!stop) setHeaderTitle(name ? `Chat mit Mentee ${name}` : `Chat mit Mentee #${mid}`);
            return;
          }
        }

        // Fallback
        if (!stop) setHeaderTitle(t('chatPopup.chatWithHR', 'Chat mit HR'));
        return;
      }

      // 3) Paar-Threads (ohne HR)
      if (ids.menteeId && (roleS === 'mentor' || roleR === 'mentee')) {
        const name = await fetchMenteeName(ids.menteeId);
        if (!stop) setHeaderTitle(name ? `Chat mit Mentee ${name}` : `Chat mit Mentee #${ids.menteeId}`);
        return;
      }
      if (ids.mentorId && (roleS === 'mentee' || roleR === 'mentor')) {
        const name = await fetchMentorName(ids.mentorId);
        if (!stop) setHeaderTitle(name ? `Chat mit Mentor ${name}` : `Chat mit Mentor #${ids.mentorId}`);
        return;
      }

      // Fallback allgemein
      if (!stop) setHeaderTitle(t('chatPopup.chat', 'Chat'));
    }

    resolveTitle();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, modeHR, channelMenteeHR, channelMentorHR, ids.menteeId, ids.mentorId, roleS, roleR, resolvedMenteeId, t]);

  /**
   * Fehlende menteeId **nur** dann ermitteln, wenn HR der Absender ist
   * und Mentee-Kanal aktiv ist. (Mentor↔HR soll keinen Mentee-Kontext tragen.)
   */
  useEffect(() => {
    let active = true;
    (async () => {
      if (channelMenteeHR && roleS === 'hr' && !ids.menteeId) {
        const mid = await resolveActiveMenteeIdForMentor(ids.mentorId, selectedMatchingId);
        if (active) setResolvedMenteeId(mid ?? null);
      } else {
        setResolvedMenteeId(null);
      }
    })();
    return () => { active = false; };
  }, [channelMenteeHR, roleS, ids.menteeId, ids.mentorId, selectedMatchingId]);

  /* ----------------------- Beim Öffnen: als gelesen markieren ----------------------- */
  useEffect(() => {
    if (!senderRole) return;

    const menteeFinal = ids.menteeId ?? resolvedMenteeId ?? null;

    if (!modeHR) {
      if (!menteeFinal || !ids.mentorId) return;
    } else {
      if (channelMentorHR && !ids.mentorId) return;
      if (channelMenteeHR && !menteeFinal) return;
      if (!channelMentorHR && !channelMenteeHR) return;
    }

    const body = !modeHR
      ? { menteeId: menteeFinal, mentorId: ids.mentorId, role: senderRole }
      : channelMentorHR
      ? { menteeId: null, mentorId: ids.mentorId, role: senderRole }
      : { menteeId: menteeFinal, mentorId: null, role: senderRole };

    axios
      .patch('/mentoring/chat/messages/mark-as-read', body, { withCredentials: true, timeout: 10000 })
      .then(() => {
        // Optional: sofortiges UI-Update für Badges/Glocke ermöglichen
        try {
          window.dispatchEvent(new CustomEvent('chat:markedRead', {
            detail: {
              scope: 'hr',
              channel: channelMentorHR ? 'mentor' : (channelMenteeHR ? 'mentee' : 'pair'),
              menteeId: menteeFinal ?? null,
              mentorId: ids.mentorId ?? null
            }
          }));
        } catch {}
      })
      .catch(() => {});
  }, [modeHR, channelMentorHR, channelMenteeHR, ids.menteeId, resolvedMenteeId, ids.mentorId, senderRole]);

  /* ----------------------- Nachrichten laden ------------------------- */
  useEffect(() => {
    let stop = false;

    const load = async () => {
      const menteeFinal = ids.menteeId ?? resolvedMenteeId ?? null;

      if (!modeHR) {
        if (!menteeFinal || !ids.mentorId) return;
      } else {
        if (channelMentorHR && !ids.mentorId) return;
        if (channelMenteeHR && !menteeFinal) return;
        if (!channelMentorHR && !channelMenteeHR) return;
      }

      const menteeKey = modeHR && channelMentorHR ? 'null' : menteeFinal === null ? 'null' : String(menteeFinal);
      const mentorKey = modeHR && channelMenteeHR ? 'null' : ids.mentorId === null ? 'null' : String(ids.mentorId);

      let data = await safeGet(`/mentoring/chat/messages/${menteeKey}/${mentorKey}`);
      let list = Array.isArray(data) ? data : [];

      // Sicherheitsfilter
      list = list.filter((m) => {
        const { menteeId: mid, mentorId: tid } = idsOf(m);
        const { s, r } = rolesOf(m);

        if (modeHR) {
          if (channelMentorHR) return tid === ids.mentorId && (s === 'hr' || r === 'hr');
          if (channelMenteeHR) return mid === (ids.menteeId ?? resolvedMenteeId ?? null) && (s === 'hr' || r === 'hr');
          return false;
        }
        return mid === (ids.menteeId ?? resolvedMenteeId ?? null) && tid === ids.mentorId && s !== 'hr' && r !== 'hr';
      });

      if (!stop) setMessages(list);
    };

    load();
    const id = setInterval(load, 5000);
    return () => { stop = true; clearInterval(id); };
  }, [modeHR, channelMentorHR, channelMenteeHR, ids.menteeId, resolvedMenteeId, ids.mentorId]);

  /* Auto-Scroll */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* Dragging */
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging) return;
      setPosition({ top: e.clientY - offset.current.y, left: e.clientX - offset.current.x });
    };
    const onMouseUp = () => setDragging(false);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging]);

  const onMouseDown = (e) => {
    setDragging(true);
    offset.current = { x: e.clientX - position.left, y: e.clientY - position.top };
  };

  /* --------------------------- File-Upload-Handler --------------------------- */
  const handleAttachClick = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    // Reset input value, damit gleiche Datei nochmal ausgewählt werden kann
    e.target.value = '';
    if (!file) return;

    // Größen-Check
    if (file.size > MAX_FILE_SIZE) {
      setErrorText(
        t(
          'chatPopup.fileTooLarge',
          'Datei zu groß. Maximum: 25 MB. Deine Datei: {{size}}',
          { size: formatFileSize(file.size) }
        )
      );
      return;
    }

    // MIME-Type-Check (kombiniert mit Extension als Fallback)
    const hasValidMime = ALLOWED_MIME_TYPES.includes(file.type);
    const nameLower = file.name.toLowerCase();
    const hasValidExt = ALLOWED_EXTENSIONS.some((ext) => nameLower.endsWith(ext));
    if (!hasValidMime && !hasValidExt) {
      setErrorText(
        t(
          'chatPopup.fileTypeNotAllowed',
          'Dateityp nicht erlaubt. Zulässig: PDF, DOCX, PPTX, XLSX, JPG, PNG'
        )
      );
      return;
    }

    setErrorText('');
    setAttachedFile({ file, previewSize: formatFileSize(file.size) });
  };

  const handleDiscardFile = () => {
    if (uploading) return;
    setAttachedFile(null);
  };

  /* ----------------------------- Senden ------------------------------ */
  const sendMessage = async () => {
    setErrorText('');
    const text = (newMessage || '').trim();

    // Early exit wenn weder Text noch File
    if (!text && !attachedFile) return;
    if (uploading) return; // Doppel-Submit-Schutz

    /* --- Branch 1: Mit File (FormData-Upload) --- */
    if (attachedFile) {
      if (!selectedMatchingId) {
        setErrorText(t('chatPopup.noPairingSelected', 'Kein aktives Pairing ausgewählt.'));
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', attachedFile.file);
        if (text) {
          formData.append('message', text);
        }
        await axios.post(
          `/mentoring/chat/${selectedMatchingId}/files`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true,
            timeout: 60000,
          }
        );
        // Success: Reset alles; Chat-Verlauf lädt per Polling automatisch nach
        setAttachedFile(null);
        setNewMessage('');
      } catch (err) {
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.error;
        if (status === 413) {
          setErrorText(
            serverMsg ||
              t('chatPopup.uploadTooLargeOrQuota', 'Datei zu groß oder Pairing-Quota erreicht.')
          );
        } else if (status === 415) {
          setErrorText(t('chatPopup.fileTypeNotAllowed', 'Dateityp nicht erlaubt.'));
        } else if (status === 401) {
          setErrorText(t('chatPopup.notLoggedIn', 'Nicht eingeloggt. Bitte neu anmelden.'));
        } else if (status === 507) {
          setErrorText(
            t('chatPopup.serverStorageFull', 'Server-Speicher voll. Bitte Jörg informieren.')
          );
        } else {
          setErrorText(
            t('chatPopup.uploadFailed', 'Upload fehlgeschlagen. Bitte erneut versuchen.')
          );
        }
        // File bleibt in Vorschau, damit Nutzer retry machen kann
      } finally {
        setUploading(false);
      }
      return; // NICHT in den Text-only-Pfad laufen
    }

    /* --- Branch 2: Nur Text (bestehender Code unverändert) --- */
    if (!text) return;

    const menteeFinal = ids.menteeId ?? resolvedMenteeId ?? null;

    if (!modeHR) {
      if (!menteeFinal || !ids.mentorId) {
        setErrorText(t('chatPopup.missingIds', 'Mentee- oder Mentor-ID fehlt.'));
        return;
      }
    } else {
      if (channelMentorHR && !ids.mentorId) {
        setErrorText(t('chatPopup.missingIds', 'Mentor-ID fehlt.'));
        return;
      }
      if (channelMenteeHR && !menteeFinal) {
        setErrorText(t('chatPopup.missingIds', 'Mentee-ID fehlt.'));
        return;
      }
      if (!channelMentorHR && !channelMenteeHR) {
        setErrorText(t('chatPopup.missingIds', 'Unklarer HR-Thread.'));
        return;
      }
    }

    try {
      const payload = !modeHR
        ? { menteeId: menteeFinal, mentorId: ids.mentorId, senderRole, receiverRole, message: text }
        : channelMentorHR
        ? { menteeId: null, mentorId: ids.mentorId, senderRole, receiverRole, message: text }
        : { menteeId: menteeFinal, mentorId: null, senderRole, receiverRole, message: text };

      const res = await axios.post('/mentoring/chat/message', payload, { withCredentials: true, timeout: 12000 });

      const appended =
        res?.data && typeof res.data === 'object'
          ? res.data
          : {
              mentee_id: payload.menteeId,
              mentor_id: payload.mentorId,
              sender_role: senderRole,
              receiver_role: receiverRole,
              message: text,
              timestamp: new Date().toISOString(),
              read: false,
            };

      setMessages((prev) => [...prev, appended]);
      setNewMessage('');
    } catch {
      setErrorText(t('chatPopup.sendError', 'Senden fehlgeschlagen. Bitte später erneut versuchen.'));
    }
  };

  if (senderRole == null || receiverRole == null) return null;

  /* UI-Label (Fallback) */
  const fallbackLabel = () => {
    if (modeHR) return t('chatPopup.chatWithHR', 'Chat mit HR');
    if (roleS === 'hr' && roleR === 'mentor') return t('chatPopup.chatWithMentor');
    if (roleS === 'hr' && roleR === 'mentee') return t('chatPopup.chatWithMentee');
    if (roleS === 'mentee' && roleR === 'mentor') return t('chatPopup.chatWithMentor');
    if (roleS === 'mentor' && roleR === 'mentee') return t('chatPopup.chatWithMentee');
    if (roleS === 'mentee' && roleR === 'hr') return t('chatPopup.chatWithHR');
    if (roleS === 'mentor' && roleR === 'hr') return t('chatPopup.chatWithHR');
    return t('chatPopup.chat', 'Chat');
  };

  /* Render */
  let lastDate = '';

  return (
    <div
      className="w-[400px] bg-white border rounded-xl shadow-lg flex flex-col z-50"
      style={{ position: 'fixed', top: position.top, left: position.left }}
    >
      <div
        onMouseDown={onMouseDown}
        className="cursor-move bg-mentixxgreen text-black p-2 font-bold flex justify-between items-center rounded-t-xl"
      >
        <span>{headerTitle || fallbackLabel()}</span>
        <button onClick={() => onClose?.()} aria-label={t('chatPopup.close', 'Schließen')}>✕</button>
      </div>

      <div className="flex-1 p-2 overflow-y-auto max-h-96 text-sm">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 mt-10">
            {t('chatPopup.noMessages', 'Noch keine Nachrichten.')}
          </div>
        )}

        {messages.map((msg, idx) => {
          const currentDate = formatDateLabel(msg.timestamp);
          const showDateLabel = currentDate !== lastDate;
          lastDate = currentDate;

          const mine =
            String(msg.sender_role ?? msg.senderRole ?? msg.sender).toLowerCase() === roleS;

          return (
            <div key={idx} className="mb-2">
              {showDateLabel && (
                <div className="text-center text-xs text-gray-500 my-2">{currentDate}</div>
              )}
              <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`inline-block px-3 py-2 rounded-lg max-w-[75%] ${
                    mine ? 'bg-mentixxgreen text-black' : 'bg-gray-200 text-black'
                  }`}
                >
                  {msg.message && <div>{convertEmojis(msg.message)}</div>}
                  {msg.file && <ChatFileAttachment file={msg.file} />}
                  <div className="text-right text-xs mt-1 text-gray-600">
                    {formatTime(msg.timestamp)}{' '}
                    {msg.read ? t('chatPopup.read', 'Gelesen') : t('chatPopup.sent', 'Gesendet')}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      <div className="p-2 border-t">
        {errorText && <div className="mb-2 text-xs text-red-600">{errorText}</div>}

        {/* Hidden native file input (triggered by paperclip) */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.pptx,.xlsx,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          disabled={uploading}
        />

        {/* Vorschau-Bubble (nur sichtbar wenn Datei angehängt) */}
        {attachedFile && (
          <div className="flex items-center gap-2 mb-2 p-2 bg-gray-100 border rounded text-sm">
            <FileText size={18} className="shrink-0 text-gray-600" />
            <div className="flex-1 min-w-0">
              <div
                className="truncate text-black"
                title={attachedFile.file.name}
              >
                {attachedFile.file.name}
              </div>
              <div className="text-xs text-gray-500">{attachedFile.previewSize}</div>
            </div>
            <button
              type="button"
              onClick={handleDiscardFile}
              disabled={uploading}
              className="p-1 text-gray-500 hover:text-red-600 disabled:opacity-50"
              aria-label={t('chatPopup.removeFile', 'Datei entfernen')}
              title={t('chatPopup.removeFile', 'Datei entfernen')}
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div className="flex gap-2 mb-2 items-center">
          <button
            type="button"
            onClick={handleAttachClick}
            disabled={uploading || !selectedMatchingId}
            className="p-1 text-gray-600 hover:text-mentixxgreen disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={t('chatPopup.attachFile', 'Datei anhängen')}
            title={t('chatPopup.attachFile', 'Datei anhängen')}
          >
            <Paperclip size={18} />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={t('chatPopup.inputPlaceholder', 'Nachricht eingeben…')}
            className="flex-1 p-1 border rounded text-black placeholder-gray-500"
            onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
            disabled={uploading}
          />
          <Button onClick={sendMessage} disabled={uploading}>
            {uploading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              t('chatPopup.send', 'Senden')
            )}
          </Button>
        </div>

        <div className="flex gap-2 text-lg">
          {EMOJI_LIST.map((emoji, idx) => (
            <button
              key={idx}
              onClick={() => setNewMessage((msg) => (msg || '') + emoji)}
              className="hover:scale-110 transition-transform"
              aria-label={t('chatPopup.addEmoji', { emoji })}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

